<template>
  <div class="wizard-root">
    <!-- Premium Step Header -->
    <div class="wizard-header">
      <div class="wizard-steps">
        <div
          v-for="(s, i) in stepConfig"
          :key="i"
          class="wizard-step"
          :class="{ active: step === i + 1, completed: step > i + 1, clickable: step > i + 1 }"
          @click="step > i + 1 && (step = i + 1)">
          <div class="step-circle">
            <v-icon v-if="step > i + 1" x-small color="white">mdi-check</v-icon>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <div class="step-label">{{ s.label }}</div>
          <div v-if="i < stepConfig.length - 1" class="step-line" :class="{ filled: step > i + 1 }"></div>
        </div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="wizard-body" :class="{ 'wizard-body-fullheight': step === 5 }">
      <!-- Step 1: Wochen-Vorlage -->
      <div v-if="step === 1" class="wizard-step-content">
        <div v-if="availableStudios.length > 1" class="mb-4" style="max-width: 320px">
          <v-select
            filled
            dense
            dark
            hide-details
            :items="availableStudios"
            label="Studio wählen"
            v-model="selectedStudio"
            item-text="name"
            item-value="_id"
            @change="handleStudioChange"
            class="wizard-select"></v-select>
        </div>
        <div v-else-if="availableStudios.length === 1 && !selectedStudio">{{ autoSelectStudio() }}</div>

        <weekly-template-planner
          v-if="!loading && selectedStudio && settings.shifts && settings.shifts.length > 0"
          :studio-id="selectedStudio"
          :available-shifts="settings.shifts"
          :key="`weekly-planner-${selectedStudio}`"
          @template-saved="handleTemplateUpdate" />
        <div v-else-if="loading" class="wizard-loading">
          <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
          <p class="mt-4" style="color: rgba(255, 255, 255, 0.4)">Lade Daten…</p>
        </div>
        <div v-else class="wizard-loading">
          <v-icon size="48" color="rgba(255,255,255,0.15)">mdi-alert-circle-outline</v-icon>
          <p class="mt-3" style="color: rgba(255, 255, 255, 0.4)">
            {{ !selectedStudio ? "Bitte Studio auswählen." : "Keine Schichtvorlagen gefunden." }}
          </p>
        </div>
      </div>

      <!-- Step 2: Spezialtage & Monat -->
      <div v-if="step === 2" class="wizard-step-content">
        <div class="d-flex align-center mb-5" style="gap: 16px">
          <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn depressed dark v-bind="attrs" v-on="on" class="wizard-month-btn">
                <v-icon left small>mdi-calendar-month</v-icon>{{ monthDisplayText }}
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-date-picker v-model="planningMonth" type="month" no-title scrollable dark @click:month="onMonthChanged"></v-date-picker>
          </v-menu>
          <span class="text-caption" style="color: rgba(255, 255, 255, 0.35)">
            {{ $moment(planningDateRange[0]).format("DD.MM") }} – {{ $moment(planningDateRange[1]).format("DD.MM.YYYY") }}
          </span>
        </div>
        <ShiftManagementMainDialogCreateSpecialDays
          v-if="selectedStudio"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :available-shifts="settings.shifts"
          :employees="employeesBasic"
          :weekly-template-data="loadedWeeklyTemplate"
          :public-holidays="publicHolidays"
          @back="step = 1"
          @next="step = 3" />
      </div>

      <!-- Step 3: Mitarbeiter -->
      <div v-if="step === 3" class="wizard-step-content">
        <shift-management-employee-settings
          v-if="selectedStudio && planningDateRange.length === 2"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :weekly-template-data="loadedWeeklyTemplate"
          :available-shifts="settings.shifts"
          :school-holidays="settings.schoolHolidays || []"
          :confirmed-employee-settings="employeesBasic"
          ref="employeeSettings"
          @next="nextStep" />
        <div v-else class="wizard-loading">
          <p style="color: rgba(255, 255, 255, 0.4)">Bitte zuerst Studio und Zeitraum wählen.</p>
          <v-btn text color="green accent-3" @click="step = 1" class="mt-3">Zurück</v-btn>
        </div>
      </div>

      <!-- Step 4: Zuweisung -->
      <div v-if="step === 4" class="wizard-step-content">
        <shift-management-assignment
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :loading="assigning"
          :finished="assignmentFinished"
          :auto-start="true"
          @trigger-assignment="triggerAssignment"
          @view-plan="step = 5"
          ref="assignmentComponent" />
      </div>

      <!-- Step 5: Einsatzplan -->
      <div v-if="step === 5" class="wizard-step-content wizard-step-fullheight">
        <shift-management-roster-view
          v-if="(assignedPlanData.length > 0 || unassignedShifts.length > 0) && assignedEmployees.length > 0"
          :plan-data="assignedPlanData"
          :studioId="selectedStudio"
          :availableShifts="settings.shifts"
          :unassignedShifts="unassignedShifts"
          :employees="assignedEmployees"
          :start-date="planningDateRange[0]"
          :school-holidays="settings.schoolHolidays || []"
          :public-holidays="publicHolidays"
          :previous-context="previousContext"
          :loading="assigning"
          @refresh="refreshData" />
        <div v-else class="wizard-loading">
          <v-icon size="48" color="rgba(255,255,255,0.15)">mdi-calendar-question</v-icon>
          <p class="mt-3" style="color: rgba(255, 255, 255, 0.4)">
            {{ assigning ? "Plan wird geladen…" : "Keine Daten. Bitte Schritt 4 wiederholen." }}
          </p>
          <v-btn v-if="!assigning" color="green accent-3" class="mt-3 black--text" depressed @click="step = 4">Zurück</v-btn>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="wizard-bottom">
      <div class="wizard-bottom-inner">
        <v-btn v-if="step !== 2" text dark @click="prevStep" :disabled="step === 1 || assigning" class="wizard-nav-btn">
          <v-icon left small>mdi-arrow-left</v-icon> Zurück
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="step === 5" depressed @click="$emit('close-dialog')" class="wizard-finish-btn">
          <v-icon left small>mdi-check-circle</v-icon> Fertigstellen & Schließen
        </v-btn>
        <v-btn v-else-if="step !== 2" depressed @click="nextStep" :disabled="step === 4" class="wizard-next-btn">
          Weiter <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="showConfirmationDialog" max-width="480px" persistent>
      <v-card dark class="rounded-xl">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="amber">mdi-alert-circle-outline</v-icon>
          <span style="font-size: 1rem">Nicht alle MA bereit</span>
        </v-card-title>
        <v-card-text class="text-body-2">
          <span v-if="confirmationDialogData.notReadyNames.length > 0">
            Folgende MA werden <strong>ignoriert</strong>:
            <div class="mt-2" style="max-height: 200px; overflow: auto">
              <v-chip v-for="name in confirmationDialogData.notReadyNames" :key="name" small outlined class="mr-1 mb-1" color="amber">{{
                name
              }}</v-chip>
            </div>
          </span>
          <div class="mt-3">
            Fortfahren mit <strong>{{ confirmationDialogData.readyEmployeeIds.length }}</strong> Mitarbeitern?
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConfirmationDialog = false">Abbrechen</v-btn>
          <v-btn color="amber" text @click="proceedToAssignment" class="font-weight-bold">Fortfahren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import WeeklyTemplatePlanner from "./WeeklyTemplatePlanner.vue"
import ShiftManagementEmployeeSettings from "./shiftManagementMainDialogCreateEmployee.vue"
import ShiftManagementAssignment from "./shiftManagementAssignment.vue"
import ShiftManagementRosterView from "./shiftManagementRosterView.vue"
import ShiftManagementMainDialogCreateSpecialDays from "./shiftManagementMainDialogCreateSpecialDays.vue"
import { mapState } from "vuex"

export default {
  name: "ArbeitsplanStepper",
  components: {
    WeeklyTemplatePlanner,
    ShiftManagementEmployeeSettings,
    ShiftManagementAssignment,
    ShiftManagementRosterView,
    ShiftManagementMainDialogCreateSpecialDays,
  },
  props: {
    preSelectedStudio: { type: String, default: null },
  },
  data() {
    return {
      step: 1,
      loading: true,
      assigning: false,
      assignmentFinished: false,
      planningMonth: this.$moment().format("YYYY-MM"),
      initialLoadComplete: false,
      dateMenu: false,
      selectedStudio: null,
      availableStudios: [],
      settings: { shifts: [] },
      loadedWeeklyTemplate: [],
      employeesBasic: [],
      assignedPlanData: [],
      assignedEmployees: [],
      unassignedShifts: [],
      publicHolidays: [],
      previousContext: null,
      showConfirmationDialog: false,
      confirmationDialogData: { notReadyNames: [], readyEmployeeIds: [] },
      confirmedEmployeeSettings: [],

      stepConfig: [{ label: "Vorlage" }, { label: "Spezialtage" }, { label: "Mitarbeiter" }, { label: "Zuweisung" }, { label: "Einsatzplan" }],
    }
  },
  async created() {
    this.loading = true
    try {
      ;[this.settings, this.availableStudios] = await Promise.all([api.shiftManagement.fetchSettings("local"), api.fetchAllowedAdminStudios()])
      if (this.availableStudios.length > 0) {
        if (this.preSelectedStudio) {
          this.selectedStudio = this.preSelectedStudio
        } else if (!this.selectedStudio) {
          this.selectedStudio = this.availableStudios[0]._id
        }
        await this.fetchWeeklyTemplate()
        await this.fetchBasicEmployees()
        await this.fetchHolidaysAndContext()
      }
    } catch (error) {
      this.$toast.error("Fehler beim Initialisieren.")
      console.error("Init Error:", error)
    } finally {
      this.loading = false
      this.initialLoadComplete = true
    }
  },
  watch: {
    selectedStudio(newStudio, oldStudio) {
      if (this.initialLoadComplete && newStudio !== oldStudio) this.fetchWeeklyTemplate()
    },
    step(newStep, oldStep) {
      if (this.initialLoadComplete && newStep === 1 && oldStep > 1) this.fetchWeeklyTemplate()
      if (newStep === 3 && this.loadedWeeklyTemplate.length === 0 && !this.loadingTemplate) this.fetchWeeklyTemplate()
      if (newStep === 4) this.assignmentFinished = false
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    planningDateRange() {
      const start = this.$moment(this.planningMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
      const end = this.$moment(this.planningMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD")
      return [start, end]
    },
    monthDisplayText() {
      return this.$moment(this.planningMonth, "YYYY-MM").format("MMMM YYYY")
    },
  },
  methods: {
    onMonthChanged(newMonth) {
      this.planningMonth = newMonth
      this.dateMenu = false
      this.$nextTick(() => {
        this.fetchHolidaysAndContext()
      })
    },
    async refreshData() {
      if (!this.selectedStudio) return
      this.assigning = true
      try {
        const response = await api.shiftManagement.fetchPlan(this.selectedStudio, this.planningDateRange[0], this.planningDateRange[1])
        const responseData = response && response.data ? response.data : response
        const freshShifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []
        if (!freshShifts) throw new Error("Keine Schicht-Daten empfangen")
        await this.handleAssignmentComplete(freshShifts, false)
      } catch (e) {
        console.error("Refresh Error:", e)
        this.$toast.error("Konnte Ansicht nicht aktualisieren.")
      } finally {
        this.assigning = false
      }
    },
    async fetchBasicEmployees() {
      if (!this.selectedStudio) return
      try {
        const emps = await api.shiftManagement.fetchShiftManagementEmployee()
        this.employeesBasic = emps.filter((e) => {
          const studioInfo = e.studio?.find((s) => String(s._id) === String(this.selectedStudio))
          return !!studioInfo
        })
      } catch (e) {
        console.error("Employees Error", e)
      }
    },
    calculatePaidVacationHours(employeeData, planningSettings) {
      if (!this.planningDateRange || this.planningDateRange.length < 2) return 0
      const start = this.$moment(this.planningDateRange[0])
      const end = this.$moment(this.planningDateRange[1])
      const studioInfo = employeeData.studio.find((s) => s._id === this.selectedStudio)
      if (!studioInfo) return 0
      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)
      const dailyHours = 8.4 * (percentage / 100)
      let vacationHours = 0
      const vacations = employeeData.vacations || []
      vacations.forEach((vac) => {
        ;(vac.dates || []).forEach((d) => {
          const dateMoment = this.$moment(d.date)
          if (dateMoment.isBetween(start, end, null, "[]")) {
            if (dateMoment.isoWeekday() <= 5) vacationHours += dailyHours
          }
        })
      })
      return vacationHours
    },
    getPlanningPercentage(employee, studioInfo, planningSettings) {
      if (!studioInfo && !employee.timeTrackerPercentage) return 0
      const planningPercentages = planningSettings?.percentages
      if (planningPercentages && planningPercentages[studioInfo._id] != null) return planningPercentages[studioInfo._id]
      if (employee.timeTrackerPercentage != null) return employee.timeTrackerPercentage
      if (studioInfo) return studioInfo.percentage
      return 0
    },
    async handleStudioChange(newStudioId) {
      if (!newStudioId || !this.initialLoadComplete) return
      this.loading = true
      await this.fetchWeeklyTemplate()
      await this.fetchBasicEmployees()
      await this.fetchHolidaysAndContext()
      this.loading = false
    },
    async fetchWeeklyTemplate() {
      if (!this.selectedStudio) {
        this.loadedWeeklyTemplate = []
        return
      }
      this.loading = true
      try {
        this.loadedWeeklyTemplate = await api.shiftManagement.fetchWeeklyTemplate(this.selectedStudio)
      } catch (error) {
        this.$toast.error("Vorlage-Ladefehler.")
        this.loadedWeeklyTemplate = []
      } finally {
        this.loading = false
      }
    },
    handleTemplateUpdate(savedTemplate) {
      this.loadedWeeklyTemplate = savedTemplate
      this.$toast.success("Vorlage aktualisiert.")
    },
    async fetchHolidaysAndContext() {
      if (!this.selectedStudio || !this.planningDateRange || this.planningDateRange.length < 2) return
      try {
        const [holidays, context] = await Promise.all([
          api.shiftManagement.fetchPublicHolidays(this.selectedStudio, this.planningDateRange[0], this.planningDateRange[1]),
          api.shiftManagement.fetchPreviousMonthContext(this.selectedStudio, this.planningDateRange[0]),
        ])
        this.publicHolidays = holidays || []
        this.previousContext = context || null
      } catch (e) {
        console.error("Holidays Error:", e)
      }
    },
    async triggerAssignment() {
      if (!this.confirmationDialogData.readyEmployeeIds || this.confirmationDialogData.readyEmployeeIds.length === 0) {
        this.$toast.error("Keine Mitarbeiter ausgewählt.")
        return
      }
      if (!this.confirmedEmployeeSettings || this.confirmedEmployeeSettings.length === 0) {
        this.$toast.error("Mitarbeiterdaten nicht gefunden.")
        return
      }
      this.assigning = true
      this.assignmentFinished = false
      const planningSettingsPayload = {}
      const [startDate, endDate] = this.planningDateRange
      try {
        const readyEmployeesData = this.confirmedEmployeeSettings.filter((emp) => this.confirmationDialogData.readyEmployeeIds.includes(emp.user))
        readyEmployeesData.forEach((employee) => {
          const settingsKey = `planningSettings_${employee._id}_${this.selectedStudio}`
          const savedSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
          const existing = employee.planningSettings || {}
          planningSettingsPayload[employee._id.toString()] = {
            percentages: savedSettings.percentages || {},
            recurringAbsence: savedSettings.recurringAbsence || [],
            ignore: savedSettings.hasOwnProperty("isReadyForPlanning") ? !savedSettings.isReadyForPlanning : existing.ignore || false,
            fixedShifts: savedSettings.fixedShifts || existing.fixedShifts || {},
            longCommute: savedSettings.longCommute || existing.longCommute || false,
          }
        })
        const generationResponse = await api.shiftManagement.generatePlanFromTemplate(this.selectedStudio, startDate, endDate)
        console.log(`[GENERATE] ${generationResponse.createdCount} erstellt.`)
        const updatedPlan = await api.shiftManagement.assignEmployeesToPlan(
          this.selectedStudio,
          startDate,
          endDate,
          planningSettingsPayload,
          this.confirmationDialogData.readyEmployeeIds,
        )
        if (!updatedPlan || updatedPlan.length === 0) throw new Error("Leerer Plan.")
        await this.handleAssignmentComplete(updatedPlan, false)
        this.assignmentFinished = true
      } catch (error) {
        this.$toast.error("Zuweisungsfehler.")
        console.error("Assignment Error:", error)
        this.handleAssignmentComplete([], false)
      } finally {
        this.assigning = false
      }
    },
    async handleAssignmentComplete(updatedPlan, switchStep = true) {
      this.assignedPlanData = updatedPlan.filter((p) => p.assignedEmployee)
      this.unassignedShifts = updatedPlan.filter((p) => !p.assignedEmployee)
      if (switchStep) this.assigning = true
      try {
        const allStudioEmployees = await api.shiftManagement.fetchShiftManagementEmployee()
        let sourceEmployees = this.confirmedEmployeeSettings
        if (!sourceEmployees || sourceEmployees.length === 0) {
          sourceEmployees = allStudioEmployees.filter((emp) => emp.studio && emp.studio.some((s) => String(s._id) === String(this.selectedStudio)))
        }
        const employeeMap = new Map(
          sourceEmployees.map((e) => {
            const uId = String(e.user._id || e.user)
            return [uId, e]
          }),
        )
        const assignedUserIds = [...new Set(this.assignedPlanData.map((p) => String(p.assignedEmployee._id || p.assignedEmployee)))]
        this.assignedEmployees = assignedUserIds
          .map((userId) => {
            let employeeData = employeeMap.get(userId)
            if (!employeeData) employeeData = allStudioEmployees.find((e) => String(e.user._id || e.user) === userId)
            if (!employeeData) return null
            const settingsKey = `planningSettings_${employeeData._id}_${this.selectedStudio}`
            const savedSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
            const planningSettings = {
              percentages: savedSettings.percentages || employeeData.planningSettings?.percentages || {},
              recurringAbsence: savedSettings.recurringAbsence || employeeData.planningSettings?.recurringAbsence || [],
              isReadyForPlanning: savedSettings.isReadyForPlanning || employeeData.planningSettings?.isReadyForPlanning || false,
            }
            return {
              ...employeeData,
              _id: userId,
              sollStunden: this.calculateSollStunden(employeeData, planningSettings),
              plannedHours:
                this.calculatePlannedHours(userId, this.assignedPlanData) + this.calculatePaidVacationHours(employeeData, planningSettings),
              planningSettings,
            }
          })
          .filter((e) => e !== null)
      } catch (error) {
        console.error("Roster Prep Error:", error)
        this.$toast.error("Fehler beim Aufbereiten.")
        this.assignedEmployees = []
      } finally {
        if (switchStep) {
          this.assigning = false
          this.step = 5
        }
      }
    },
    calculateSollStunden(employeeData, planningSettings) {
      if (!this.planningDateRange || this.planningDateRange.length < 2 || !employeeData) return 0
      const start = this.$moment(this.planningDateRange[0])
      const end = this.$moment(this.planningDateRange[1])
      let businessDays = 0
      let current = start.clone()
      const studioInfo = employeeData.studio.find((s) => s._id === this.selectedStudio)
      if (!studioInfo) return 0
      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)
      const publicHolidayDates = new Set((this.publicHolidays || []).filter((h) => !h.isWeekend).map((h) => h.date))
      while (current.isSameOrBefore(end, "day")) {
        const dayOfWeekISO = current.isoWeekday()
        const isWeekend = dayOfWeekISO >= 6
        const dateStr = current.format("YYYY-MM-DD")
        const isSchoolHoliday = (this.settings.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]"),
        )
        const isPublicHoliday = publicHolidayDates.has(dateStr) && !isWeekend
        let isSchoolDay = false
        if (employeeData.isApprentice && !isSchoolHoliday) {
          isSchoolDay = (employeeData.schoolDays || []).some((sd) => sd.dayOfWeek === dayOfWeekISO)
        }
        let isWorkDay = !isWeekend && !isSchoolDay && !isPublicHoliday
        if (employeeData.isApprentice && isSchoolHoliday) isWorkDay = false
        if (isWorkDay) businessDays++
        current.add(1, "day")
      }
      return businessDays * (8.4 * (percentage / 100))
    },
    calculatePlannedHours(userId, planData) {
      let totalHours = 0
      planData.forEach((plan) => {
        const planUserId = String(plan.assignedEmployee._id || plan.assignedEmployee)
        if (planUserId === String(userId)) {
          const timesToUse = plan.times && plan.times.length > 0 ? plan.times : plan.shiftTemplate ? plan.shiftTemplate.times : []
          if (timesToUse) {
            timesToUse.forEach((time) => {
              if (time.type === "Arbeitszeit" && time.start && time.end) {
                const startTime = this.$moment(time.start, "HH:mm")
                const endTime = this.$moment(time.end, "HH:mm")
                if (endTime.isBefore(startTime)) endTime.add(1, "day")
                totalHours += endTime.diff(startTime, "hours", true)
              }
            })
          }
        }
      })
      return totalHours
    },
    nextStep() {
      if (this.step === 3) {
        if (this.$refs.employeeSettings) {
          const status = this.$refs.employeeSettings.getConfirmationStatus()
          this.confirmationDialogData.notReadyNames = status.notReadyNames
          this.confirmationDialogData.readyEmployeeIds = status.readyEmployeeIds
          this.confirmedEmployeeSettings = this.$refs.employeeSettings.employees || []
          if (!status.allMarked) {
            this.showConfirmationDialog = true
            return
          }
        }
      }
      if (this.step < 5) this.step++
    },
    prevStep() {
      if (this.step > 1) this.step--
    },
    proceedToAssignment() {
      this.showConfirmationDialog = false
      this.step = 4
    },
    autoSelectStudio() {
      if (this.availableStudios.length === 1 && !this.selectedStudio) {
        this.$nextTick(() => {
          this.selectedStudio = this.availableStudios[0]._id
        })
      }
      return ""
    },
  },
}
</script>

<style scoped>
.wizard-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: #1a1a1a;
  color: white;
}

/* === HEADER === */
.wizard-header {
  flex-shrink: 0;
  padding: 16px 32px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.wizard-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 700px;
  margin: 0 auto;
}
.wizard-step {
  display: flex;
  align-items: center;
  flex: 1;
  position: relative;
}
.wizard-step.clickable {
  cursor: pointer;
}
.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
}
.wizard-step.active .step-circle {
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  color: white;
  border-color: #4caf50;
  box-shadow: 0 0 16px rgba(76, 175, 80, 0.4);
}
.wizard-step.completed .step-circle {
  background: #2e7d32;
  color: white;
  border-color: #2e7d32;
}
.step-label {
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 8px;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.3);
  transition: color 0.3s;
}
.wizard-step.active .step-label {
  color: #66bb6a;
}
.wizard-step.completed .step-label {
  color: rgba(255, 255, 255, 0.5);
}
.step-line {
  flex: 1;
  height: 2px;
  margin: 0 12px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 1px;
  transition: background 0.5s;
}
.step-line.filled {
  background: #2e7d32;
}

/* === BODY === */
.wizard-body {
  flex: 1;
  overflow: auto;
  padding: 24px 32px 100px;
}
.wizard-body-fullheight {
  padding: 0;
}
.wizard-step-content {
  width: 100%;
  animation: fadeSlideIn 0.3s ease;
}
.wizard-step-fullheight {
  height: 100%;
  display: flex;
  flex-direction: column;
}
@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.wizard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
}
.wizard-select {
  border-radius: 10px !important;
}
.wizard-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.06) !important;
  border-radius: 10px !important;
}
.wizard-month-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 16px !important;
}

/* === BOTTOM BAR === */
.wizard-bottom {
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 12px 32px;
}
.wizard-bottom-inner {
  display: flex;
  align-items: center;
  max-width: 100%;
}
.wizard-nav-btn {
  text-transform: none !important;
  letter-spacing: 0.3px !important;
}
.wizard-next-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3) !important;
}
.wizard-finish-btn {
  background: linear-gradient(135deg, #2196f3, #1565c0) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.3) !important;
}
</style>
